import React, { useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Seo from '../components/Seo'

const inputBase = {
  width: '100%',
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid #e9ecef',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  background: 'white'
}

const inputStyle = (error) => ({
  ...inputBase,
  borderColor: error ? '#E67E22' : '#e9ecef'
})

const textareaStyle = (error) => ({
  ...inputBase,
  resize: 'vertical',
  borderColor: error ? '#E67E22' : '#e9ecef'
})

const selectStyle = { ...inputBase, appearance: 'none' }
const errStyle = { color: '#E67E22' }
const submitBtnStyle = (disabled) => ({
  backgroundColor: disabled ? '#6c8f55' : '#87A96B',
  color: 'white',
  border: 'none',
  borderRadius: '9999px',
  padding: '0.9rem 1.4rem',
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: '0 4px 15px rgba(135,169,107,0.25)'
})
const acceptedTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.zip'

export default function Contact() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ firstname: '', lastname: '', email: '', phone: '', projectType: '', message: '', consent: false })
  const [files, setFiles] = useState([])
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const totalSize = useMemo(() => files.reduce((sum, f) => sum + f.size, 0), [files])
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2)

  const validate = () => {
    const e = {}
    if (!form.firstname.trim()) e.firstname = 'Prénom requis'
    if (!form.lastname.trim()) e.lastname = 'Nom requis'
    if (!form.email.trim() && !form.phone.trim()) {
      e.contact = 'Email ou téléphone requis'
    } else {
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide'
      if (form.phone && !/^[0-9+().\-\s]{6,}$/.test(form.phone)) e.phone = 'Téléphone invalide'
    }
    if (!form.message.trim()) e.message = 'Message requis'

    const PER_FILE_MAX = 8 * 1024 * 1024
    const TOTAL_MAX = 20 * 1024 * 1024
    for (const f of files) {
      if (f.size > PER_FILE_MAX) {
        e.files = `Le fichier "${f.name}" est trop volumineux (> 8 Mo)`
        break
      }
    }
    if (!e.files && totalSize > TOTAL_MAX) {
      e.files = 'Taille totale des pièces jointes > 20 Mo'
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }
  const addFiles = (incomingFiles) => {
    const list = Array.from(incomingFiles || [])
    if (list.length === 0) return
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + '::' + f.size))
      const merged = [...prev]
      for (const f of list) {
        const key = f.name + '::' + f.size
        if (seen.has(key)) continue
        merged.push(f)
        seen.add(key)
      }
      return merged
    })
  }
  const onFiles = (e) => {
    if (e?.target?.files) addFiles(e.target.files)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files)
  }
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true) }
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false) }
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx))

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const formData = new FormData()
      Object.entries(form).forEach(([k, v]) => {
        if (v === undefined || v === null) return
        if (typeof v === 'boolean') return formData.append(k, v ? 'true' : 'false')
        formData.append(k, String(v))
      })

      files.forEach((f) => {
        formData.append('files', f, f.name)
      })

      formData.append('type', 'yaqam_com')
      formData.append('submittedAt', new Date().toISOString())

      try {
        const entries = []
        for (const pair of formData.entries()) {
          if (pair[1] instanceof File) entries.push([pair[0], pair[1].name])
          else entries.push([pair[0], pair[1]])
        }
        console.debug('Contact form data entries:', entries)
      } catch (dbgErr) {
        console.debug('Could not list FormData entries', dbgErr)
      }

      let posted = false
      let serverErrorMessage = null

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)

      try {
        const apiBase = process.env.REACT_APP_API_URL || ''
        const endpoint = apiBase ? `${apiBase}/api/public/contact` : '/api/public/contact'
        const res = await fetch(endpoint, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        })
        clearTimeout(timeoutId)

        if (res.ok) {
          posted = true
        } else {
          try {
            const body = await res.json()
            const errMsg = body?.error || `Erreur serveur (${res.status})`
            let details = ''
            if (Array.isArray(body?.details)) details = '\n' + body.details.join('\n')
            else if (typeof body?.details === 'string') details = '\n' + body.details
            serverErrorMessage = `${errMsg}${details}`
            console.warn('Contact API error response', body)
          } catch (parseErr) {
            serverErrorMessage = `Erreur serveur: ${res.status} ${res.statusText}`
            console.warn('Failed to parse error response', parseErr)
          }
        }
      } catch (err) {
        clearTimeout(timeoutId)
        if (err.name === 'AbortError') serverErrorMessage = 'Requête annulée (timeout)'
        else serverErrorMessage = `Erreur réseau ou connexion refusée : ${err.message}`
      }

      if (!posted) {
        const payload = { ...form, files: files.map(f => ({ name: f.name, size: f.size, type: f.type })), submittedAt: new Date().toISOString() }
        const existing = JSON.parse(localStorage.getItem('lead_submissions') || '[]')
        existing.push(payload)
        localStorage.setItem('lead_submissions', JSON.stringify(existing))
        const alertMsg = serverErrorMessage || "Envoi non confirmé par le serveur — votre demande a été sauvegardée localement."
        try { window.alert(alertMsg) } catch (_) {}
        setErrors(prev => ({ ...prev, submit: alertMsg }))
      } else {
        navigate('/')
      }
    } catch (err) {
      console.error('Contact submit error', err)
      setErrors(prev => ({ ...prev, submit: "Impossible d'envoyer pour le moment. Réessayez." }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <Seo 
        title="Contact & Devis"
        description="Contactez-nous pour votre projet"
        image="/img/logo_header-FR.png"
        type="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          'headline': 'Contact & Devis',
          'description': 'Contactez-nous pour votre projet',
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': typeof window !== 'undefined' ? window.location.href : ''
          },
        }}
      />
      <div className="sm:container mx-auto px-4 mt-6">
        <p className="text-center text-sm text-gray-700">N'hésitez pas à nous contacter pour toute question ou demande — nous sommes à votre écoute.</p>
      </div>

      <section style={{ maxWidth: '900px', margin: '0 auto', padding: '3rem 2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          <div style={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <form onSubmit={onSubmit} noValidate style={{ padding: '1.5rem' }}>
              {errors.submit && (<div style={{ background: '#fff3f3', color: '#E67E22', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem' }}>{errors.submit}</div>)}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="firstname" style={{ fontWeight: 600 }}>Prénom *</label>
                  <input id="firstname" name="firstname" value={form.firstname} onChange={onChange} style={inputStyle(errors.firstname)} />
                  {errors.firstname && <small style={errStyle}>{errors.firstname}</small>}
                </div>
                <div>
                  <label htmlFor="lastname" style={{ fontWeight: 600 }}>Nom *</label>
                  <input id="lastname" name="lastname" value={form.lastname} onChange={onChange} style={inputStyle(errors.lastname)} />
                  {errors.lastname && <small style={errStyle}>{errors.lastname}</small>}
                </div>
                <div>
                  <label htmlFor="email" style={{ fontWeight: 600 }}>Email {!(form.phone) && '*'}</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={onChange} placeholder="vous@exemple.com" style={inputStyle(errors.email || errors.contact)} />
                  {(errors.email || errors.contact) && <small style={errStyle}>{errors.email || errors.contact}</small>}
                </div>
                <div>
                  <label htmlFor="phone" style={{ fontWeight: 600 }}>Téléphone {!(form.email) && '*'}</label>
                  <input id="phone" name="phone" value={form.phone} onChange={onChange} placeholder="06 12 34 56 78" style={inputStyle(errors.phone || errors.contact)} />
                  {(errors.phone || errors.contact) && <small style={errStyle}>{errors.phone || errors.contact}</small>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label htmlFor="projectType" style={{ fontWeight: 600 }}>Type de projet</label>
                  <select id="projectType" name="projectType" value={form.projectType} onChange={onChange} style={selectStyle}>
                    <option value="">— Sélectionner —</option>
                    <option>Charpente traditionnelle</option>
                    <option>Charpente fermette</option>
                    <option>Construction bois</option>
                    <option>Couverture tuiles</option>
                    <option>Couverture zinc / zinguerie</option>
                    <option>Façade bois</option>
                    <option>Façade composite</option>
                    <option>Façade zinc</option>
                    <option>Isolation</option>
                    <option>Menuiserie</option>
                    <option>Autre</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label htmlFor="message" style={{ fontWeight: 600 }}>Votre message *</label>
                <textarea id="message" name="message" value={form.message} onChange={onChange} rows={6} placeholder="Décrivez votre projet (dimensions, matériaux, délais)…" style={textareaStyle(errors.message)} />
                {errors.message && <small style={errStyle}>{errors.message}</small>}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label htmlFor="files" style={{ fontWeight: 600 }}>Pièces jointes (max 8 Mo par fichier, 20 Mo au total)</label>
                <div
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragEnter={onDragOver}
                  onDragLeave={onDragLeave}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  role="button"
                  tabIndex={0}
                  style={{
                    marginTop: '0.5rem',
                    padding: '1.25rem',
                    borderRadius: '12px',
                    border: dragOver ? '2px dashed #AE1F31' : '2px dashed rgba(0,0,0,0.08)',
                    background: dragOver ? '#fff6f6' : '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  <input ref={fileInputRef} id="files" name="files" type="file" multiple onChange={onFiles} accept={acceptedTypes} style={{ display: 'none' }} />
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: '#FDF1F1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#AE1F31', fontSize: '20px' }}>⬆️</div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#333', marginBottom: '0.25rem' }}>Glissez-déposez vos fichiers ici, ou cliquez pour sélectionner</div>
                    <div style={{ color: '#666' }}>Formats acceptés : PDF, JPG, PNG, DOCX, ZIP — 8 Mo par fichier, 20 Mo au total</div>
                  </div>
                </div>
                {errors.files && <small style={errStyle}>{errors.files}</small>}
                {files.length > 0 && (
                  <div style={{ marginTop: '0.5rem', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px' }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: '0.5rem 0.75rem' }}>
                      {files.map((f, i) => (
                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0' }}>
                          <span style={{ color: '#333' }}>{f.name} — {(f.size / 1024).toFixed(0)} Ko</span>
                          <button type="button" onClick={() => removeFile(i)} style={{ background: 'transparent', color: '#AE1F31', border: '1px solid #AE1F31', borderRadius: '6px', padding: '0.25rem 0.6rem', cursor: 'pointer' }}>Retirer</button>
                        </li>
                      ))}
                    </ul>
                    <div style={{ padding: '0 0.75rem 0.5rem', color: '#666' }}>Taille totale : {totalSizeMB} Mo</div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <button type="submit" disabled={submitting} style={submitBtnStyle(submitting)}>{submitting ? 'Envoi…' : 'Envoyer ma demande'}</button>
              </div>
              <p style={{ color: '#666', marginTop: '0.5rem' }}>* Champs obligatoires</p>
            </form>
          </div>
          <div style={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <h3 style={{ color: '#E67E22', marginBottom: '0.75rem' }}>Nos coordonnées</h3>
            <address style={{ fontStyle: 'normal', color: '#333', lineHeight: 1.8 }}>
              <strong>J7LAB - YAQAM</strong><br/>10 chemin du lac<br/>43000 Polignac<br/>
              <a href="mailto:info@j7lab.com" style={{ color: '#E67E22', textDecoration: 'none' }}>info@j7lab.com</a>
            </address>
          </div>
        </div>
      </section>
    </div>
  )
}
